import { parseDate, Language, Loadable, SimpleXHR } from './util';

interface SubRatingCategoryData {
	id : string;
	name : string;
}

export class SubRatingCategory {
	private id : string;
	private name : string;
	constructor(category : SubRatingCategoryData) {
		this.id = category.id;
		this.name = category.name;
	}

	public getId() {
		return this.id;
	}

	public getName() {
		return this.name;
	}
}

interface CategorySubratingData {
	rating : number;
	ratingValue : number;
	category : SubRatingCategoryData;
}

export class CategorySubratings {
	private ratings : CategorySubratingData[];
	private subRatings : {
		[categoryId : string] : CategorySubratingData;
	};
	constructor(ratings : CategorySubratingData[]) {
		this.ratings = ratings || [];
		this.subRatings = {};

		for(const rating of this.ratings) {
			// FIXME NEC: Is there a spec for this? Rating seems to be an integer [10..50]
			rating.ratingValue = rating.rating / 10;
			this.subRatings[rating.category.id] = rating;
		}
	}

	public hasSubratings() {
		return !!this.ratings.length;
	}

	public forCategoryWithId(categoryId : string) {
		const subrating = this.subRatings[categoryId];
		if(!subrating) {
			return -0;
		}

		return subrating.ratingValue || -0;
	}

	public map<T>(mapper : (ratingValue : number, subratingCategory : SubRatingCategory) => T) {
		return this.ratings.map(rating => mapper(rating.ratingValue, new SubRatingCategory(rating.category)));
	}
}

interface VoteData {
	numHelpful : number;
	numUseless : number;
}

export abstract class Votable {
	public type = '';

	private _hasVoted : boolean | null = null;
	private _hasSentAbuseReport = false;

	public getUpvotes() {
		return this.getModel().numHelpful + (this._hasVoted === true ? 1 : 0);
	}
	public getDownvotes() {
		return this.getModel().numUseless + (this._hasVoted === false ? 1 : 0);
	}
	public hasVoted() {
		return this._hasVoted !== null;
	}
	public getVote() {
		return this._hasVoted;
	}
	public hasSentAbuseReport() {
		return this._hasSentAbuseReport;
	}

	// Attention: updates state
	public vote(isUpvote : boolean) {
		this._hasVoted = isUpvote;
	}
	public sendAbuseReport() {
		this._hasSentAbuseReport = true;
	}

	protected abstract getModel() : VoteData;
}

interface CommentData extends VoteData {
	id : string;
	reviewerName? : string;
	comment? : string;
	date : string;
	isAdminComment : boolean;
}

export class RatingComment extends Votable {
	public type = 'comment';

	private comment : CommentData;

	constructor(comment : CommentData) {
		super();
		this.comment = comment;
	}
	public getId() {
		return this.comment.id;
	}
	public getName() {
		return this.comment.reviewerName || '';
	}
	public getComment() {
		return this.comment.comment || '';
	}
	public getDate() {
		return parseDate(this.comment.date);
	}
	public isAdminComment() {
		return this.comment.isAdminComment;
	}

	// From Votable
	protected getModel() {
		return this.comment;
	}
}

interface RatingData extends VoteData {
	id : string;
	rating : number;
	language : Language;
	productId : string;
	firstName? : string;
	date? : string;
	lastName? : string;
	comment? : string;
	headline? : string;
	subratings : CategorySubratingData[];
	comments : CommentData[];
	recommended : 'YES' | 'NO' | null;
}

export class Rating extends Votable {
	public type = 'rating';

	// FIXME: Add localstorage support for persistency
	private _hasCommented : boolean;

	private rating : RatingData;
	private comments : RatingComment[];

	constructor(rating : RatingData) {
		super();
		this.rating = rating;
		this._hasCommented = false;
		this.comments = (rating.comments || []).map(function(comment) {
			return new RatingComment(comment);
		});
	}
	public getId() {
		return this.rating.id;
	}
	public getProductId() {
		return this.rating.productId;
	}
	public getRating() {
		// FIXME NEC: Is there a spec for this? Rating seems to be an integer [10..50]
		return this.rating.rating / 10;
	}
	public getFirstName() {
		return this.rating.firstName;
	}
	public getLastName() {
		return this.rating.lastName;
	}
	public getComment() {
		return this.rating.comment || '';
	}
	public getDate() {
		return parseDate(this.rating.date);
	}
	public isRecommended() {
		return String(this.rating.recommended).toUpperCase() === 'YES';
	}
	public isDiscommended() {
		return String(this.rating.recommended).toUpperCase() === 'NO';
	}
	public isRecommendationNeutral() {
		return !this.isRecommended() && !this.isDiscommended();
	}
	public getLanguage() {
		return this.rating.language || 'de';
	}
	public getHeadline() {
		return this.rating.headline;
	}
	public getCategorySubratings() {
		return new CategorySubratings(this.rating.subratings);
	}
	public getComments() {
		return this.comments;
	}
	public hasCommented() {
		return this._hasCommented;
	}

	// Attention: updates state
	public comment() {
		this._hasCommented = true;
	}

	// From Votable
	protected getModel() {
		return this.rating;
	}
}

/**
 * Wrapper object for Ratings
 */
export class SubRatingCategories {
	private subratingCategories : SubRatingCategoryData[];
	constructor(subratingCategories : SubRatingCategoryData[]) {
		this.subratingCategories = subratingCategories;
	}

	public getCategories() {
		return (this.subratingCategories || []).map(function(category) {
			return new SubRatingCategory(category);
		});
	}
}

function num(num : number, rounded? : boolean) {
	if(rounded) {
		return Math.round(num || 0);
	}

	return num || 0;
}

interface RatingAggregate {
	sum : number;
	countRecommended : number;
	count : number;
	1 : number;
	2 : number;
	3 : number;
	4 : number;
	5 : number;
}

export class RatingSummary {
	private aggregate : RatingAggregate;

	constructor(ratings : Rating[]) {
		this.aggregate = {
			1: 0,
			2: 0,
			3: 0,
			4: 0,
			5: 0,
			count: 0,
			countRecommended: 0,
			sum: 0,
		};
		for(const rating of ratings) {
			const value = rating.getRating();
			this.aggregate.count += 1;
			this.aggregate.sum += value;
			this.aggregate.countRecommended += rating.isRecommended() ? 1 : 0;
			this.aggregate[String(value) as keyof RatingAggregate] += 1;
		}
	}

	public getAverage(rounded? : boolean) {
		return num(this.aggregate.sum / this.aggregate.count, rounded);
	}

	public count(forStar? : keyof RatingAggregate | undefined) {
		let num = this.aggregate.count;
		if(forStar && forStar in this.aggregate) {
			num = this.aggregate[forStar];
		}

		return num || -0;
	}

	public getRecommendationPercentage(rounded? : boolean) {
		return num(this.aggregate.countRecommended / this.aggregate.count * 100, rounded);
	}
}


const lang : Language = $('html').attr('lang') as Language;

interface RatingsData {
	productRatings : RatingData[];
}

interface SubRatingCategoryDataWrapper {
	categories : SubRatingCategoryData[]
}

const OCC_ENDPOINT = '/portal-services/occ/';
const RATING_ENDPOINT = OCC_ENDPOINT + 'ratings/{languageId}/{productId}';
const SUBRATING_CATEGORIES_ENDPOINT = OCC_ENDPOINT + 'subratingCategories/{languageId}/{productId}';
/**
 * Load ratings from endpoint
 */
function loadRatings(url : string, productId : string) : SimpleXHR {
	if(!productId) {
		// Wrongly-configured product data hub
		// Return a promise that never resolves.
		return $.Deferred().promise() as SimpleXHR;
	}

	return $.ajax(
		url.replace('{productId}', productId).replace('{languageId}', lang),
		{
			dataType: 'json',
		},
	);
}

export type Wrapper2<T, U> = [T, U];
/**
 * Wraps all ratign-related properties
 */
export class Ratings extends Loadable<
	RatingsData[],
	SubRatingCategoryDataWrapper[],
	Wrapper2<Rating[], SubRatingCategories>
> {
	public productFamilyId : string;

	public ratings? : Rating[];
	public subRatingCategories? : SubRatingCategories;

	constructor(productFamilyId : string) {
		super();
		this.productFamilyId = productFamilyId;
	}

	public getRatings() {
		return this.ratings;
	}
	public getSummary() {
		return new RatingSummary(this.ratings || []);
	}
	public getSubRatingCategories() {
		return this.subRatingCategories;
	}

	// From Loadable
	protected loadFunction() {
		return $.when(
			// Load both infos in parallel
			loadRatings(RATING_ENDPOINT, this.productFamilyId),
			loadRatings(SUBRATING_CATEGORIES_ENDPOINT, this.productFamilyId),
		) as JQuery.Promise2<
			RatingsData[], JQuery.jqXHR<any[]>, never,
			SubRatingCategoryDataWrapper[], JQuery.jqXHR<any[]>, never
		>;
	}

	// From Loadable
	protected loadCallback(
		productRatings : RatingsData[],
		subRatingCategories : SubRatingCategoryDataWrapper[],
	) : Wrapper2<Rating[], SubRatingCategories> {
		this.ratings = (productRatings[0].productRatings || []).map((rating : RatingData) => {
			return new Rating(rating);
		});
		this.subRatingCategories = new SubRatingCategories(subRatingCategories[0].categories);

		return [this.ratings, this.subRatingCategories];
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Rating.ts